import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import type { BiddingEligibilityResponse } from '@certi/shared/types/types.user'
import { FaCheckCircle } from 'react-icons/fa'
import { MdEmail, MdPhone, MdLocationOn, MdCreditCard, MdArrowBack } from 'react-icons/md'
import { UserAPI } from '@certi/shared/api/user/UserAPI'
import { useAuth } from '@/hooks/useAuth'


interface VerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  eligibility: BiddingEligibilityResponse | null;
}

// Define all possible requirements in a fixed order, now with icons
const ALL_REQUIREMENTS = [
  { key: 'email', text: 'Verify your email', icon: <MdEmail className="w-5 h-5 text-gray-400" /> },
  { key: 'phone', text: 'Add your phone number', icon: <MdPhone className="w-5 h-5 text-gray-400" /> },
  { key: 'address', text: 'Add a shipping address', link: '/profile/addresses', icon: <MdLocationOn className="w-5 h-5 text-gray-400" /> },
  { key: 'payment', text: 'Add a payment method', link: '/profile/cards', icon: <MdCreditCard className="w-5 h-5 text-gray-400" /> },
]

// Country list for reuse across components
const COUNTRY_LIST = [
  { code: 'AF', phone: '+93' },
  { code: 'AL', phone: '+355' },
  { code: 'DZ', phone: '+213' },
  { code: 'AS', phone: '+1-684' },
  { code: 'AD', phone: '+376' },
  { code: 'AO', phone: '+244' },
  { code: 'AI', phone: '+1-264' },
  { code: 'AQ', phone: '+672' },
  { code: 'AG', phone: '+1-268' },
  { code: 'AR', phone: '+54' },
  { code: 'AM', phone: '+374' },
  { code: 'AW', phone: '+297' },
  { code: 'AU', phone: '+61' },
  { code: 'AT', phone: '+43' },
  { code: 'AZ', phone: '+994' },
  { code: 'BS', phone: '+1-242' },
  { code: 'BH', phone: '+973' },
  { code: 'BD', phone: '+880' },
  { code: 'BB', phone: '+1-246' },
  { code: 'BY', phone: '+375' },
  { code: 'BE', phone: '+32' },
  { code: 'BZ', phone: '+501' },
  { code: 'BJ', phone: '+229' },
  { code: 'BM', phone: '+1-441' },
  { code: 'BT', phone: '+975' },
  { code: 'BO', phone: '+591' },
  { code: 'BA', phone: '+387' },
  { code: 'BW', phone: '+267' },
  { code: 'BR', phone: '+55' },
  { code: 'IO', phone: '+246' },
  { code: 'VG', phone: '+1-284' },
  { code: 'BN', phone: '+673' },
  { code: 'BG', phone: '+359' },
  { code: 'BF', phone: '+226' },
  { code: 'BI', phone: '+257' },
  { code: 'KH', phone: '+855' },
  { code: 'CM', phone: '+237' },
  { code: 'CA', phone: '+1' },
  { code: 'CV', phone: '+238' },
  { code: 'KY', phone: '+1-345' },
  { code: 'CF', phone: '+236' },
  { code: 'TD', phone: '+235' },
  { code: 'CL', phone: '+56' },
  { code: 'CN', phone: '+86' },
  { code: 'CX', phone: '+61' },
  { code: 'CC', phone: '+61' },
  { code: 'CO', phone: '+57' },
  { code: 'KM', phone: '+269' },
  { code: 'CK', phone: '+682' },
  { code: 'CR', phone: '+506' },
  { code: 'HR', phone: '+385' },
  { code: 'CU', phone: '+53' },
  { code: 'CW', phone: '+599' },
  { code: 'CY', phone: '+357' },
  { code: 'CZ', phone: '+420' },
  { code: 'CD', phone: '+243' },
  { code: 'DK', phone: '+45' },
  { code: 'DJ', phone: '+253' },
  { code: 'DM', phone: '+1-767' },
  { code: 'DO', phone: '+1-809' },
  { code: 'TL', phone: '+670' },
  { code: 'EC', phone: '+593' },
  { code: 'EG', phone: '+20' },
  { code: 'SV', phone: '+503' },
  { code: 'GQ', phone: '+240' },
  { code: 'ER', phone: '+291' },
  { code: 'EE', phone: '+372' },
  { code: 'ET', phone: '+251' },
  { code: 'FK', phone: '+500' },
  { code: 'FO', phone: '+298' },
  { code: 'FJ', phone: '+679' },
  { code: 'FI', phone: '+358' },
  { code: 'FR', phone: '+33' },
  { code: 'PF', phone: '+689' },
  { code: 'GA', phone: '+241' },
  { code: 'GM', phone: '+220' },
  { code: 'GE', phone: '+995' },
  { code: 'DE', phone: '+49' },
  { code: 'GH', phone: '+233' },
  { code: 'GI', phone: '+350' },
  { code: 'GR', phone: '+30' },
  { code: 'GL', phone: '+299' },
  { code: 'GD', phone: '+1-473' },
  { code: 'GU', phone: '+1-671' },
  { code: 'GT', phone: '+502' },
  { code: 'GG', phone: '+44-1481' },
  { code: 'GN', phone: '+224' },
  { code: 'GW', phone: '+245' },
  { code: 'GY', phone: '+592' },
  { code: 'HT', phone: '+509' },
  { code: 'HN', phone: '+504' },
  { code: 'HK', phone: '+852' },
  { code: 'HU', phone: '+36' },
  { code: 'IS', phone: '+354' },
  { code: 'IN', phone: '+91' },
  { code: 'ID', phone: '+62' },
  { code: 'IR', phone: '+98' },
  { code: 'IQ', phone: '+964' },
  { code: 'IE', phone: '+353' },
  { code: 'IM', phone: '+44-1624' },
  { code: 'IL', phone: '+972' },
  { code: 'IT', phone: '+39' },
  { code: 'CI', phone: '+225' },
  { code: 'JM', phone: '+1-876' },
  { code: 'JP', phone: '+81' },
  { code: 'JE', phone: '+44-1534' },
  { code: 'JO', phone: '+962' },
  { code: 'KZ', phone: '+7' },
  { code: 'KE', phone: '+254' },
  { code: 'KI', phone: '+686' },
  { code: 'XK', phone: '+383' },
  { code: 'KW', phone: '+965' },
  { code: 'KG', phone: '+996' },
  { code: 'LA', phone: '+856' },
  { code: 'LV', phone: '+371' },
  { code: 'LB', phone: '+961' },
  { code: 'LS', phone: '+266' },
  { code: 'LR', phone: '+231' },
  { code: 'LY', phone: '+218' },
  { code: 'LI', phone: '+423' },
  { code: 'LT', phone: '+370' },
  { code: 'LU', phone: '+352' },
  { code: 'MO', phone: '+853' },
  { code: 'MK', phone: '+389' },
  { code: 'MG', phone: '+261' },
  { code: 'MW', phone: '+265' },
  { code: 'MY', phone: '+60' },
  { code: 'MV', phone: '+960' },
  { code: 'ML', phone: '+223' },
  { code: 'MT', phone: '+356' },
  { code: 'MH', phone: '+692' },
  { code: 'MR', phone: '+222' },
  { code: 'MU', phone: '+230' },
  { code: 'YT', phone: '+262' },
  { code: 'MX', phone: '+52' },
  { code: 'FM', phone: '+691' },
  { code: 'MD', phone: '+373' },
  { code: 'MC', phone: '+377' },
  { code: 'MN', phone: '+976' },
  { code: 'ME', phone: '+382' },
  { code: 'MS', phone: '+1-664' },
  { code: 'MA', phone: '+212' },
  { code: 'MZ', phone: '+258' },
  { code: 'MM', phone: '+95' },
  { code: 'NA', phone: '+264' },
  { code: 'NR', phone: '+674' },
  { code: 'NP', phone: '+977' },
  { code: 'NL', phone: '+31' },
  { code: 'AN', phone: '+599' },
  { code: 'NC', phone: '+687' },
  { code: 'NZ', phone: '+64' },
  { code: 'NI', phone: '+505' },
  { code: 'NE', phone: '+227' },
  { code: 'NG', phone: '+234' },
  { code: 'NU', phone: '+683' },
  { code: 'KP', phone: '+850' },
  { code: 'MP', phone: '+1-670' },
  { code: 'NO', phone: '+47' },
  { code: 'OM', phone: '+968' },
  { code: 'PK', phone: '+92' },
  { code: 'PW', phone: '+680' },
  { code: 'PS', phone: '+970' },
  { code: 'PA', phone: '+507' },
  { code: 'PG', phone: '+675' },
  { code: 'PY', phone: '+595' },
  { code: 'PE', phone: '+51' },
  { code: 'PH', phone: '+63' },
  { code: 'PN', phone: '+64' },
  { code: 'PL', phone: '+48' },
  { code: 'PT', phone: '+351' },
  { code: 'PR', phone: '+1-787' },
  { code: 'QA', phone: '+974' },
  { code: 'CG', phone: '+242' },
  { code: 'RE', phone: '+262' },
  { code: 'RO', phone: '+40' },
  { code: 'RU', phone: '+7' },
  { code: 'RW', phone: '+250' },
  { code: 'BL', phone: '+590' },
  { code: 'SH', phone: '+290' },
  { code: 'KN', phone: '+1-869' },
  { code: 'LC', phone: '+1-758' },
  { code: 'MF', phone: '+590' },
  { code: 'PM', phone: '+508' },
  { code: 'VC', phone: '+1-784' },
  { code: 'WS', phone: '+685' },
  { code: 'SM', phone: '+378' },
  { code: 'ST', phone: '+239' },
  { code: 'SA', phone: '+966' },
  { code: 'SN', phone: '+221' },
  { code: 'RS', phone: '+381' },
  { code: 'SC', phone: '+248' },
  { code: 'SL', phone: '+232' },
  { code: 'SG', phone: '+65' },
  { code: 'SX', phone: '+1-721' },
  { code: 'SK', phone: '+421' },
  { code: 'SI', phone: '+386' },
  { code: 'SB', phone: '+677' },
  { code: 'SO', phone: '+252' },
  { code: 'ZA', phone: '+27' },
  { code: 'KR', phone: '+82' },
  { code: 'SS', phone: '+211' },
  { code: 'ES', phone: '+34' },
  { code: 'LK', phone: '+94' },
  { code: 'SD', phone: '+249' },
  { code: 'SR', phone: '+597' },
  { code: 'SJ', phone: '+47' },
  { code: 'SZ', phone: '+268' },
  { code: 'SE', phone: '+46' },
  { code: 'CH', phone: '+41' },
  { code: 'SY', phone: '+963' },
  { code: 'TW', phone: '+886' },
  { code: 'TJ', phone: '+992' },
  { code: 'TZ', phone: '+255' },
  { code: 'TH', phone: '+66' },
  { code: 'TG', phone: '+228' },
  { code: 'TK', phone: '+690' },
  { code: 'TO', phone: '+676' },
  { code: 'TT', phone: '+1-868' },
  { code: 'TN', phone: '+216' },
  { code: 'TR', phone: '+90' },
  { code: 'TM', phone: '+993' },
  { code: 'TC', phone: '+1-649' },
  { code: 'TV', phone: '+688' },
  { code: 'VI', phone: '+1-340' },
  { code: 'UG', phone: '+256' },
  { code: 'UA', phone: '+380' },
  { code: 'AE', phone: '+971' },
  { code: 'GB', phone: '+44' },
  { code: 'US', phone: '+1' },
  { code: 'UY', phone: '+598' },
  { code: 'UZ', phone: '+998' },
  { code: 'VU', phone: '+678' },
  { code: 'VA', phone: '+379' },
  { code: 'VE', phone: '+58' },
  { code: 'VN', phone: '+84' },
  { code: 'WF', phone: '+681' },
  { code: 'EH', phone: '+212' },
  { code: 'YE', phone: '+967' },
  { code: 'ZM', phone: '+260' },
  { code: 'ZW', phone: '+263' }
]

// Helper function to get phone code from country code
const getPhoneCode = (countryCode: string): string => {
  const country = COUNTRY_LIST.find(c => c.code === countryCode);
  return country?.phone || '+1';
}

interface SimplePhoneAddViewProps {
  setView: (view: 'checklist' | 'phone_verify' | 'code_verify' | 'simple_phone_add') => void;
  countryCode: string;
  setCountryCode: (code: string) => void;
  phoneNumber: string;
  setPhoneNumber: (phone: string) => void;
  userId: string;
  token: string;
  onPhoneAdded: () => void;
}

interface PhoneVerificationViewProps {
  setView: (view: 'checklist' | 'phone_verify' | 'code_verify' | 'simple_phone_add') => void;
  countryCode: string;
  setCountryCode: (code: string) => void;
  phoneNumber: string;
  setPhoneNumber: (phone: string) => void;
  userId: string;
  token: string;
}

const PhoneVerificationView: React.FC<PhoneVerificationViewProps> = ({ setView, countryCode, setCountryCode, phoneNumber, setPhoneNumber, userId, token }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Build country options from the shared list
  const countryOptions = useMemo(() => {
    return COUNTRY_LIST.map(country => ({
      value: country.code, // Use country code as unique value
      label: `${country.code} ${country.phone}`,
      countryCode: country.code,
      phoneCode: country.phone
    }));
  }, []);

  const handleSendCode = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter a phone number');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await UserAPI.sendPhoneVerificationCode(userId, countryCode, phoneNumber, token);
      setView('code_verify');
    } catch (err) {
      console.error('Failed to send verification code:', err);
      setError(err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  return(
  <div>
    {/* Header with Back Button */}
    <div className="flex items-center mb-4">
      <button onClick={() => setView('checklist')} className="p-1 rounded-full hover:bg-gray-100 mr-2">
        <MdArrowBack className="w-5 h-5 text-gray-600" />
      </button>
      <h3 className="text-xl font-semibold text-gray-900">Verify your phone number</h3>
    </div>

    <p className="text-sm text-gray-600 mb-6">
      To confirm your account, we'll send a verification code to your phone.
    </p>

    <div className="flex gap-2">
      <div className="w-1/3">
        <label htmlFor="country-code" className="block text-xs font-medium text-gray-500 mb-1">Code</label>
        <select
          id="country-code"
          value={countryCode}
          onChange={(e) => setCountryCode(e.target.value)}
          className="w-full h-12 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4D2A] focus:border-transparent transition bg-white"
        >
          {countryOptions.map(option => (
            <option key={option.countryCode} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
      <div className="w-2/3">
        <label htmlFor="phone-number" className="block text-xs font-medium text-gray-500 mb-1">&nbsp;</label>
        <input
          type="tel"
          id="phone-number"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
          className="w-full h-12 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4D2A] focus:border-transparent transition"
          placeholder="Phone number"
        />
      </div>
    </div>

    {error && (
      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
        {error}
      </div>
    )}

    <button
      onClick={handleSendCode}
      disabled={loading}
      className="w-full mt-6 bg-[#0B4D2A] text-white font-semibold py-3 rounded-lg hover:bg-opacity-90 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0B4D2A] disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {loading ? 'Sending...' : 'Send Code'}
    </button>
  </div>
  )
};

// Temporary simple phone add view (no OTP verification)
const SimplePhoneAddView: React.FC<SimplePhoneAddViewProps> = ({ setView, countryCode, setCountryCode, phoneNumber, setPhoneNumber, userId, token, onPhoneAdded }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Build country options from the shared list
  const countryOptions = useMemo(() => {
    return COUNTRY_LIST.map(country => ({
      value: country.code,
      label: `${country.code} ${country.phone}`,
      countryCode: country.code,
      phoneCode: country.phone
    }));
  }, []);

  const handleAddPhone = async () => {
    if (!phoneNumber.trim()) {
      setError('Please enter a phone number');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get the phone code for the selected country
      const phoneCode = getPhoneCode(countryCode);
      // Combine phone code and number for storage
      const fullPhone = `${phoneCode} ${phoneNumber}`;

      // Update user profile with phone number
      await UserAPI.updateProfile(userId, { phone: fullPhone }, token);
      onPhoneAdded();
    } catch (err) {
      console.error('Failed to add phone number:', err);
      setError(err instanceof Error ? err.message : 'Failed to add phone number');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Header with Back Button */}
      <div className="flex items-center mb-4">
        <button onClick={() => setView('checklist')} className="p-1 rounded-full hover:bg-gray-100 mr-2">
          <MdArrowBack className="w-5 h-5 text-gray-600" />
        </button>
        <h3 className="text-xl font-semibold text-gray-900">Add your phone number</h3>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Please provide your phone number to continue.
      </p>

      <div className="flex gap-2">
        <div className="w-1/3">
          <label htmlFor="country-code" className="block text-xs font-medium text-gray-500 mb-1">Code</label>
          <select
            id="country-code"
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            className="w-full h-12 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4D2A] focus:border-transparent transition bg-white"
          >
            {countryOptions.map(option => (
              <option key={option.countryCode} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div className="w-2/3">
          <label htmlFor="phone-number" className="block text-xs font-medium text-gray-500 mb-1">&nbsp;</label>
          <input
            type="tel"
            id="phone-number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className="w-full h-12 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4D2A] focus:border-transparent transition"
            placeholder="Phone number"
          />
        </div>
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      <button
        onClick={handleAddPhone}
        disabled={loading}
        className="w-full mt-6 bg-[#0B4D2A] text-white font-semibold py-3 rounded-lg hover:bg-opacity-90 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0B4D2A] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Adding...' : 'Add Number'}
      </button>
    </div>
  );
};


interface CodeVerificationViewProps {
  setView: (view: 'checklist' | 'phone_verify' | 'code_verify' | 'simple_phone_add') => void;
  phoneNumber: string;
  phoneCode: string;
  countryCode: string;
  userId: string;
  token: string;
  onVerificationComplete: () => void;
}

interface EmailVerificationViewProps {
  setView: (view: 'checklist' | 'email_verify' | 'phone_verify' | 'code_verify' | 'simple_phone_add') => void;
  userEmail: string;
}

const EmailVerificationView: React.FC<EmailVerificationViewProps> = ({ setView, userEmail }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  // Send email on mount
  useEffect(() => {
    sendEmail();
  }, []);

  const sendEmail = async () => {
    setLoading(true);
    setError(null);

    try {
      await UserAPI.sendEmailVerification(userEmail);
      setEmailSent(true);
    } catch (err) {
      console.error('Failed to send verification email:', err);
      setError(err instanceof Error ? err.message : 'Failed to send verification email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Header with Back Button */}
      <div className="flex items-center mb-4">
        <button onClick={() => setView('checklist')} className="p-1 rounded-full hover:bg-gray-100 mr-2">
          <MdArrowBack className="w-5 h-5 text-gray-600" />
        </button>
        <h3 className="text-xl font-semibold text-gray-900">Verify Your Email</h3>
      </div>

      {loading && !emailSent ? (
        <div className="text-center py-8">
          <p className="text-gray-600">Sending verification email...</p>
        </div>
      ) : error ? (
        <div>
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {error}
          </div>
          <button
            onClick={sendEmail}
            disabled={loading}
            className="w-full bg-[#0B4D2A] text-white font-semibold py-3 rounded-lg hover:bg-opacity-90 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0B4D2A] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Sending...' : 'Retry'}
          </button>
        </div>
      ) : (
        <div>
          <p className="text-sm text-gray-600 mb-6">
            An email with a verification link has been sent to <strong>{userEmail}</strong>
          </p>

          <button
            onClick={sendEmail}
            disabled={loading}
            className="w-full text-sm text-[#0B4D2A] hover:underline transition-colors disabled:opacity-50 disabled:cursor-not-allowed mb-3"
          >
            {loading ? 'Sending...' : 'Resend verification link'}
          </button>
        </div>
      )}
    </div>
  );
};

const CodeVerificationView: React.FC<CodeVerificationViewProps> = ({ setView, phoneNumber, phoneCode, countryCode, userId, token, onVerificationComplete }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRefs = [
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
  ];

  const handleCodeChange = (index: number, value: string) => {
    // Only allow single digit
    if (value.length > 1) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handleVerifyCode = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await UserAPI.verifyPhoneCode(userId, fullCode, countryCode, phoneNumber, token);
      onVerificationComplete();
    } catch (err) {
      console.error('Failed to verify code:', err);
      setError(err instanceof Error ? err.message : 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError(null);
    setCode(['', '', '', '', '', '']);

    try {
      await UserAPI.sendPhoneVerificationCode(userId, countryCode, phoneNumber, token);
      // Focus first input
      inputRefs[0].current?.focus();
    } catch (err) {
      console.error('Failed to resend code:', err);
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Header with Back Button */}
      <div className="flex items-center mb-4">
        <button onClick={() => setView('phone_verify')} className="p-1 rounded-full hover:bg-gray-100 mr-2">
          <MdArrowBack className="w-5 h-5 text-gray-600" />
        </button>
        <h3 className="text-xl font-semibold text-gray-900">Enter verification code</h3>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        We've sent a verification code to {phoneCode} {phoneNumber}. Please enter it below.
      </p>

      {/* 6 Code Input Boxes */}
      <div className="flex gap-2 justify-center mb-6">
        {code.map((digit, index) => (
          <input
            key={index}
            ref={inputRefs[index]}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleCodeChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            className="w-12 h-12 text-center text-xl font-semibold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4D2A] focus:border-[#0B4D2A] transition"
          />
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      <button
        onClick={handleVerifyCode}
        disabled={loading}
        className="w-full bg-[#0B4D2A] text-white font-semibold py-3 rounded-lg hover:bg-opacity-90 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0B4D2A] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Verifying...' : 'Verify Code'}
      </button>

      <button
        onClick={handleResendCode}
        disabled={loading}
        className="w-full mt-3 text-sm text-gray-600 hover:text-[#0B4D2A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Resend code
      </button>
    </div>
  );
};

const VerificationModal: React.FC<VerificationModalProps> = ({ isOpen, onClose, eligibility }) => {
  const { user, accessToken, session } = useAuth();
  const token = accessToken ?? session?.accessToken ?? '';

  const [view, setView] = useState<'checklist' | 'email_verify' | 'phone_verify' | 'code_verify' | 'simple_phone_add'>('checklist');
  const [countryCode, setCountryCode] = useState('US'); // Store country code (US, CA, etc.)
  const [phoneNumber, setPhoneNumber] = useState('');

  const handleComplete = () => {
    // Close modal and refresh page or eligibility
    onClose();
    window.location.reload(); // Simple refresh to update eligibility
  };

  const handleEmailVerification = () => {
    if (!user?.email) return;
    // Switch to email verification view
    setView('email_verify');
  };

  // Reset view when modal is closed/reopened
  useEffect(() => {
    if (isOpen) {
      setView('checklist');
    }
  }, [isOpen]);


  if (!isOpen || !eligibility) {
    return null
  }

  const missingSet = new Set(eligibility.missingRequirements.map(req => req.toLowerCase()));

  const handleClose = () => {
    setView('checklist'); // Also reset view on close
    onClose();
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center auth-overlay z-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 m-4 w-full max-w-md">
        {view === 'checklist' && (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">Verification Process</h3>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Checklist */}
            <div className="space-y-3">
              {ALL_REQUIREMENTS.map((req) => {
                const isMissing = [...missingSet].some(missingReq => missingReq.includes(req.key.toLowerCase()));

                // For email verification, use a button to switch to email view
                if (isMissing && req.key === 'email') {
                  return (
                    <button
                      key={req.key}
                      onClick={handleEmailVerification}
                      className="w-full flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group text-left"
                    >
                      <div className="flex items-center gap-3">
                        {req.icon}
                        <span className="text-sm font-medium text-gray-700">{req.text}</span>
                      </div>
                      <svg
                        className="w-4 h-4 text-gray-400 group-hover:text-[#0B4D2A] transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )
                }

                // For the phone number, use a button to switch views instead of a link
                if (isMissing && req.key === 'phone') {
                  return (
                    <button
                      key={req.key}
                      onClick={() => setView('simple_phone_add')}
                      className="w-full flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group text-left"
                    >
                      <div className="flex items-center gap-3">
                        {req.icon}
                        <span className="text-sm font-medium text-gray-700">{req.text}</span>
                      </div>
                      <svg
                        className="w-4 h-4 text-gray-400 group-hover:text-[#0B4D2A] transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )
                }

                if (isMissing && req.link) {
                  return (
                    <Link
                      key={req.key}
                      to={req.link}
                      onClick={handleClose} // Close modal on click
                      className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        {req.icon}
                        <span className="text-sm font-medium text-gray-700">{req.text}</span>
                      </div>
                      <svg
                        className="w-4 h-4 text-gray-400 group-hover:text-[#0B4D2A] transition-colors"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  )
                }

                return (
                  <div
                    key={req.key}
                    className="flex items-center p-3 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <FaCheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-sm font-medium text-gray-700">{req.text}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}

        {view === 'email_verify' && user && (
          <EmailVerificationView
            setView={setView}
            userEmail={user.email}
          />
        )}

        {view === 'phone_verify' && user && (
          <PhoneVerificationView
            setView={setView}
            countryCode={countryCode}
            setCountryCode={setCountryCode}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            userId={user.id}
            token={token}
          />
        )}

        {view === 'code_verify' && user && (
          <CodeVerificationView
            setView={setView}
            phoneCode={getPhoneCode(countryCode)}
            phoneNumber={phoneNumber}
            countryCode={countryCode}
            userId={user.id}
            token={token}
            onVerificationComplete={handleComplete}
          />
        )}

        {view === 'simple_phone_add' && user && (
          <SimplePhoneAddView
            setView={setView}
            countryCode={countryCode}
            setCountryCode={setCountryCode}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            userId={user.id}
            token={token}
            onPhoneAdded={handleComplete}
          />
        )}
      </div>
    </div>
  )
}

export default VerificationModal
